/**
 * 工程文件（`.svgedit.json`）：SVG 源 + 视图态 + 列表折叠态 + 锁定/选中集。
 *
 * 两条设计决定：
 *  1) **文件内部不含时间戳等易变字段** —— 时间放文件名里。
 *     这样 PLAN 的验收项「导出 → 导入 → 再导出**字节一致**」是可以真的成立的。
 *  2) 锁定状态不存成 DOM 属性（导出会剥掉），而存**元素路径**（索引链）——
 *     与编辑器一贯的寻址方式一致，且不会因为别的工具重排属性而失效。
 */
import { nodeAt, pathOf } from '../dom';

export const PROJECT_FORMAT = 'svg-edit-lite/project';
export const PROJECT_VERSION = 1;
export const PROJECT_EXT = '.svgedit.json';

export interface ProjectState {
  name: string;
  view: { zoom: number; panX: number; panY: number };
  /** 分组折叠态：groupKey → 是否折叠 */
  collapsed: Record<string, boolean>;
  /** 锁定元素（路径，文档序） */
  locked: number[][];
  /** 选中元素（路径，选择顺序） */
  selection: number[][];
  /** 双击进入的分组上下文路径 */
  context: number[] | null;
}

export interface ProjectFile extends ProjectState {
  format: string;
  version: number;
  /** 整篇 SVG 源（编辑器私有属性已剥） */
  svg: string;
}

export class ProjectError extends Error {}

const isNumArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every((n) => typeof n === 'number' && Number.isFinite(n));

const isPaths = (v: unknown): v is number[][] => Array.isArray(v) && v.every(isNumArray);

/** 固定字段序构造（决定序列化字节） */
export function buildProject(svgText: string, state: ProjectState): ProjectFile {
  const collapsed: Record<string, boolean> = {};
  for (const key of Object.keys(state.collapsed).sort()) {
    if (state.collapsed[key]) collapsed[key] = true; // 只记"折叠了"的，语义明确、字节稳定
  }
  return {
    format: PROJECT_FORMAT,
    version: PROJECT_VERSION,
    name: state.name,
    svg: svgText,
    view: { zoom: state.view.zoom, panX: state.view.panX, panY: state.view.panY },
    collapsed,
    locked: state.locked.map((p) => [...p]),
    selection: state.selection.map((p) => [...p]),
    context: state.context ? [...state.context] : null,
  };
}

/** 序列化（2 空格缩进；字段序固定 ⇒ 同样的状态必然得到同样的字节） */
export function projectToText(project: ProjectFile): string {
  return `${JSON.stringify(project, null, 2)}\n`;
}

/** 解析并校验；格式不对就抛 ProjectError（带人话原因） */
export function parseProject(text: string): ProjectFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new ProjectError('不是合法的 JSON 文件');
  }
  if (typeof raw !== 'object' || raw === null) throw new ProjectError('工程文件内容不是对象');
  const obj = raw as Record<string, unknown>;

  if (obj.format !== PROJECT_FORMAT) throw new ProjectError('不是本编辑器的工程文件（format 不匹配）');
  if (typeof obj.version !== 'number' || obj.version > PROJECT_VERSION) {
    throw new ProjectError(`工程文件版本 ${String(obj.version)} 高于当前支持的 ${PROJECT_VERSION}`);
  }
  if (typeof obj.svg !== 'string' || !obj.svg.includes('<svg')) {
    throw new ProjectError('工程文件缺少有效的 SVG 源');
  }
  const view = (obj.view ?? {}) as Record<string, unknown>;
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

  return buildProject(obj.svg, {
    name: typeof obj.name === 'string' ? obj.name : '未命名',
    view: { zoom: num(view.zoom, 1), panX: num(view.panX, 0), panY: num(view.panY, 0) },
    collapsed: (typeof obj.collapsed === 'object' && obj.collapsed !== null
      ? (obj.collapsed as Record<string, boolean>)
      : {}) as Record<string, boolean>,
    locked: isPaths(obj.locked) ? obj.locked : [],
    selection: isPaths(obj.selection) ? obj.selection : [],
    context: isNumArray(obj.context) ? obj.context : null,
  });
}

/** 路径 → 元素（失效路径静默跳过：文档可能已被改过） */
export function resolvePaths(root: Element, paths: readonly (readonly number[])[]): Element[] {
  const out: Element[] = [];
  for (const path of paths) {
    const el = nodeAt(root, path);
    if (el) out.push(el);
  }
  return out;
}

/** 元素路径（以根元素为参照；失效返回 null） */
export function pathOfElement(root: Element, el: Element): number[] | null {
  const path = pathOf(el, root);
  return path ? [...path] : null;
}

/** 导出文件名：时间放这儿，而不是放进文件内容 */
export function projectFilename(name: string, now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const base = name.replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '_') || '未命名';
  return `${base}-${stamp}${PROJECT_EXT}`;
}
